import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import { CARD_STATES, SENTIMENTS, TRANSLATION_KEY, VERDICT_OUTCOMES, WATCHLIST_FILTERS } from '../constants';
import { monitorShape } from '../propShapes';
import { getDisplayVerdict } from '../utils';
import css from './WatchlistFilters.css';

const FILTERS_KEY = `${TRANSLATION_KEY}.FILTERS`;

const CHIPS = [
    {
        key: 'HEADS_UP',
        filter: WATCHLIST_FILTERS.HEADS_UP,
        testId: 'summary-heads-up',
        dotClass: 'dotRed',
        matches: verdict => verdict?.sentiment === SENTIMENTS.HEADS_UP,
    },
    {
        key: 'GOOD_NEWS',
        filter: WATCHLIST_FILTERS.GOOD_NEWS,
        testId: 'summary-good-news',
        dotClass: 'dotGreen',
        matches: verdict => verdict?.sentiment === SENTIMENTS.GOOD_NEWS,
    },
    {
        key: 'FLAGGED',
        filter: WATCHLIST_FILTERS.FLAGGED,
        testId: 'summary-flagged',
        dotClass: 'dotGray',
        matches: verdict => verdict?.should_notify === true,
    },
    {
        key: 'PROMPT_UNCLEAR',
        filter: WATCHLIST_FILTERS.PROMPT_UNCLEAR,
        testId: 'summary-needs-reply',
        dotClass: 'dotBlue',
        matches: verdict => verdict?.outcome === VERDICT_OUTCOMES.PROMPT_UNCLEAR,
    },
];

const chipFor = filter => CHIPS.find(chip => chip.filter === filter) ?? null;

const isCountable = (state, filter) =>
    state === CARD_STATES.VERDICT ||
    state === CARD_STATES.BUSY ||
    (state === CARD_STATES.PAUSED && filter === WATCHLIST_FILTERS.PROMPT_UNCLEAR);

/**
 * Whether a monitor belongs under the given filter. `ALL` keeps every monitor, including the
 * ones still checking that no result-based chip can count yet.
 */
export const matchesFilter = (monitor, filter, cardStates, liveRuns) => {
    if (filter === WATCHLIST_FILTERS.ALL) return true;
    const chip = chipFor(filter);
    if (!chip) return true;
    if (!isCountable(cardStates.get(monitor.monitor_id), filter)) return false;
    return chip.matches(getDisplayVerdict(monitor, liveRuns[monitor.session_id]));
};

const WatchlistFilters = ({ monitors, cardStates, liveRuns, filter, shownCount, onFilterChange }) => {
    const countFor = chip => monitors.filter(m => matchesFilter(m, chip.filter, cardStates, liveRuns)).length;
    const isAll = filter === WATCHLIST_FILTERS.ALL;

    return (
        <div className={css.filters}>
            <button
                type="button"
                aria-pressed={isAll}
                className={classNames(css.chip, { [css.chipSelected]: isAll })}
                onClick={() => onFilterChange(WATCHLIST_FILTERS.ALL)}
            >
                <Translate id={`${FILTERS_KEY}.ALL`} data={{ count: monitors.length }} />
            </button>
            {CHIPS.map(chip => {
                const count = countFor(chip);
                if (count === 0) return null;
                const selected = filter === chip.filter;
                return (
                    <button
                        key={chip.key}
                        type="button"
                        data-testid={chip.testId}
                        aria-pressed={selected}
                        className={classNames(css.chip, { [css.chipSelected]: selected })}
                        onClick={() => onFilterChange(selected ? WATCHLIST_FILTERS.ALL : chip.filter)}
                    >
                        <span className={classNames(css.dot, css[chip.dotClass])} />
                        <Translate id={`${TRANSLATION_KEY}.SUMMARY.${chip.key}`} data={{ count }} />
                    </button>
                );
            })}
            {!isAll && (
                <div className={css.note}>
                    <Translate id={`${FILTERS_KEY}.NOTE`} data={{ shown: shownCount, total: monitors.length }} />
                </div>
            )}
        </div>
    );
};

WatchlistFilters.propTypes = {
    monitors: PropTypes.arrayOf(monitorShape).isRequired,
    cardStates: PropTypes.instanceOf(Map).isRequired,
    liveRuns: PropTypes.objectOf(PropTypes.object).isRequired,
    filter: PropTypes.oneOf(Object.values(WATCHLIST_FILTERS)).isRequired,
    shownCount: PropTypes.number.isRequired,
    onFilterChange: PropTypes.func.isRequired,
};

export default React.memo(WatchlistFilters);
